import { CircleAlert, RefreshCw } from 'lucide-react'

import { CenteredPanel, CenteredScreen } from '@/components/layout/centered-screen'
import { Button } from '@/components/ui/button'
import type { ErrorRecoveryKind } from '@/features/system/error-recovery'

export function TenantAccessDeniedScreen({ status, onLogout }: { status: string; onLogout: () => void }) {
  const message =
    status === 'preActive'
      ? '契約が完了していません。管理画面より契約を完了してください。'
      : status === 'free'
        ? 'お試しプランではスマートフォンアプリをご利用いただけません。ミルシートの閲覧は管理画面をご利用ください。'
        : 'このアカウントは有効ではありません。'
  return (
    <CenteredScreen>
      <CenteredPanel className="text-center">
        <CircleAlert className="mx-auto" aria-hidden="true" />
        <h1 className="text-xl font-bold">Mill-Boxを利用できません</h1>
        <p>{message}</p>
        <Button variant="outline" onClick={onLogout}>
          別のアカウントでログイン
        </Button>
      </CenteredPanel>
    </CenteredScreen>
  )
}

export function ErrorRecoveryScreen({
  kind,
  message,
  onHome,
  onReload,
  onSignOut,
}: {
  kind: ErrorRecoveryKind
  message?: string
  onHome: () => void
  onReload: () => void
  onSignOut: () => void
}) {
  const stale = kind === 'stale-client'
  const signOutOnly = kind === 'unauthenticated' || kind === 'not-found'
  return (
    <CenteredScreen>
      <CenteredPanel className="text-center" role="alert">
        <CircleAlert className="mx-auto" aria-hidden="true" />
        <h1 className="text-xl font-bold">{stale ? '新しいバージョンがあります' : 'エラーが発生しました'}</h1>
        <p>{stale ? '画面を更新して、もう一度操作してください。' : message}</p>
        {stale ? (
          <Button onClick={onReload}>
            <RefreshCw />
            画面を更新
          </Button>
        ) : signOutOnly ? (
          <Button onClick={onSignOut}>ログインし直す</Button>
        ) : (
          <>
            <Button onClick={onHome}>トップへ戻る</Button>
            <Button variant="outline" onClick={onReload}>
              <RefreshCw />
              画面を更新
            </Button>
          </>
        )}
      </CenteredPanel>
    </CenteredScreen>
  )
}

/**
 * アプリの更新（新しいバージョンの配信）を、未保存の写真が残っている間だけ保留していることを知らせる通知。
 *
 * スキャン中に更新が降ってくると、端末内にしか無い写真が失われるため、
 * `scan-session-context` が未保存キューを持っている間は更新を止めてこの通知を出す。
 * つまり「撮影直後にリリースが重なった」ときだけ出る通知で、単独の画面ではない。
 */
export function RefreshPendingNotice({
  onRetry,
  status,
  unsavedCount,
}: {
  onRetry: () => void
  status: 'saving' | 'blocked'
  unsavedCount: number
}) {
  return (
    // スマホ幅では左右に等しい余白を持つ横長のトースト、広い画面では右下に寄せる
    <aside
      className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-sm rounded-lg border bg-background p-4 shadow-lg sm:inset-x-auto sm:right-3"
      role="status"
    >
      <p className="font-bold">
        {status === 'blocked' ? '保存に失敗したため更新できません' : 'アプリの更新を保留しています'}
      </p>
      <p className="text-sm">未保存の写真 {unsavedCount} 枚を保存した後、自動的に更新します。</p>
      {status === 'blocked' && (
        <Button className="mt-3 w-full" onClick={onRetry}>
          保存を再試行
        </Button>
      )}
    </aside>
  )
}

export function SimpleMessageScreen({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <CenteredScreen>
      <CenteredPanel className="text-center">
        <h1 className="text-xl font-bold">{title}</h1>
        <div>{children}</div>
      </CenteredPanel>
    </CenteredScreen>
  )
}
